import { getPageImage } from "@/lib/page-imagery";
const aboutHero = getPageImage("/about");
import { EditorialHero } from "./EditorialHero";
import { FaqSection } from "./FaqSection";
import { LangLink } from "./LangLink";
import { content, whatsappUrl } from "@/lib/content";
import { useLanguage } from "@/lib/language";

const work = [
  {
    title: { en: "Start with the requirement", ar: "نبدأ بالمتطلب" },
    text: {
      en: "Identify the buyer, standard, activities, sites and deadline. Agree the preparation scope before building documents.",
      ar: "نحدّد المشتري والمواصفة والأنشطة والمواقع والموعد، ونتفق على نطاق التحضير قبل إعداد الوثائق.",
    },
  },
  {
    title: { en: "Build with your team", ar: "نبني مع فريقك" },
    text: {
      en: "Work with managers and process owners on responsibilities, procedures and records that reflect how the organization operates.",
      ar: "نعمل مع المديرين ومسؤولي العمليات على المسؤوليات والإجراءات والسجلات التي تعكس طريقة عمل المؤسسة.",
    },
  },
  {
    title: { en: "Review readiness", ar: "نراجع الجاهزية" },
    text: {
      en: "Review the available evidence, gaps and corrective actions. Keep ownership with the people who will use the system.",
      ar: "نراجع الأدلة المتوفرة والفجوات والإجراءات التصحيحية، مع إبقاء المسؤولية لدى الأشخاص الذين سيستخدمون النظام.",
    },
  },
  {
    title: { en: "Coordinate the next step", ar: "ننسّق الخطوة التالية" },
    text: {
      en: "Confirm the proposed certification body and relevant scope, or the buyer’s registration route. Those organizations make their own assessment and acceptance decisions.",
      ar: "نؤكّد جهة منح الشهادات المقترحة ونطاقها، أو مسار التسجيل لدى المشتري. وتتخذ تلك الجهات قرارات التقييم والقبول بصورة مستقلة.",
    },
  },
];
const roles = [
  {
    title: { en: "Your team", ar: "فريقك" },
    text: {
      en: "Own the processes, apply the procedures and maintain the evidence.",
      ar: "يتولى مسؤولية العمليات وتطبيق الإجراءات والمحافظة على الأدلة.",
    },
  },
  {
    title: { en: "IraqCert", ar: "عراق سيرت" },
    text: {
      en: "Support preparation, documentation and coordination within the agreed scope.",
      ar: "تدعم التحضير والتوثيق والتنسيق ضمن النطاق المتفق عليه.",
    },
  },
  {
    title: { en: "The certification body", ar: "جهة منح الشهادات" },
    text: {
      en: "Conduct the certification assessment and decide whether to issue a certificate.",
      ar: "تجري تقييم منح الشهادة وتتخذ قرار إصدارها.",
    },
  },
  {
    title: { en: "The buyer", ar: "المشتري" },
    text: {
      en: "Determine supplier registration, certificate acceptance and procurement decisions.",
      ar: "يحدّد تسجيل الموردين وقبول الشهادات وقرارات المشتريات.",
    },
  },
];
const cities = [
  {
    slug: "erbil",
    name: { en: "Erbil", ar: "أربيل" },
    detail: {
      en: "Operating base and KRI company requirements",
      ar: "مقر العمل ومتطلبات شركات الإقليم",
    },
  },
  {
    slug: "baghdad",
    name: { en: "Baghdad", ar: "بغداد" },
    detail: {
      en: "Public procurement and buyer requirements",
      ar: "المشتريات العامة ومتطلبات المشترين",
    },
  },
  {
    slug: "basra",
    name: { en: "Basra", ar: "البصرة" },
    detail: { en: "Oil and gas supplier readiness", ar: "جاهزية موردي النفط والغاز" },
  },
];
const label = "text-xs font-semibold uppercase tracking-widest text-primary";
const section = "mx-auto max-w-7xl px-6 py-14 sm:px-8 sm:py-20";
export function AboutContent() {
  const { t } = useLanguage();
  const a = content.about;
  return (
    <>
      <EditorialHero image={aboutHero.src} alt={t(aboutHero.alt)}>
        <p className={label}>{t(a.hero.eyebrow)}</p>
        <h1 className="mt-5 max-w-xl text-4xl sm:text-5xl">
          {t({
            en: "Management systems, built with your team.",
            ar: "أنظمة إدارة نبنيها مع فريقك.",
          })}
        </h1>
        <p className="mt-6 max-w-lg text-base text-muted-foreground sm:text-lg">
          {t({
            en: "Based in Erbil, IraqCert supports smaller and larger organizations across Iraq with management-system preparation, ISO certification coordination and supplier registration.",
            ar: "تدعم عراق سيرت، من مقر عملها في أربيل، المؤسسات الصغيرة والكبيرة في أنحاء العراق في التحضير لأنظمة الإدارة وتنسيق شهادات الأيزو وتسجيل الموردين.",
          })}
        </p>
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-8 inline-flex min-h-12 items-center rounded-md bg-primary px-6 py-3 text-base font-semibold text-primary-foreground"
        >
          {t(a.hero.cta)}
        </a>
      </EditorialHero>
      <section className={section} aria-labelledby="about-work">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.3fr] lg:gap-20">
          <div>
            <p className={label}>
              {t({ en: "The work behind the certificate", ar: "العمل الذي يسبق الشهادة" })}
            </p>
            <h2 id="about-work" className="mt-4 text-3xl sm:text-4xl">
              {t({
                en: "Clear processes. Shared responsibility.",
                ar: "عمليات واضحة ومسؤولية مشتركة.",
              })}
            </h2>
            <p className="mt-5 text-muted-foreground">
              {t({
                en: "A management system needs to work for the people using it. We connect the requirement to your organization’s activities, team and records rather than treating certification as a document purchase.",
                ar: "يجب أن يكون نظام الإدارة مناسباً للأشخاص الذين يستخدمونه. نربط المتطلب بأنشطة مؤسستك وفريقها وسجلاتها، بدلاً من التعامل مع الشهادة كشراء وثيقة.",
              })}
            </p>
            <LangLink
              to="/services"
              className="mt-6 inline-flex min-h-11 items-center font-semibold text-primary underline underline-offset-4"
            >
              {t({ en: "Explore the preparation services", ar: "اطّلع على خدمات التحضير" })}
            </LangLink>
          </div>
          <ol className="divide-y divide-border border-y border-border">
            {work.map((s, i) => (
              <li key={s.title.en} className="grid grid-cols-[2rem_1fr] gap-4 py-6">
                <span className="text-sm font-semibold text-primary">0{i + 1}</span>
                <div>
                  <h3 className="text-xl">{t(s.title)}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{t(s.text)}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>
      <section className="bg-[#f5f3ef]" aria-labelledby="about-experience">
        <div className={section}>
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-20">
            <div>
              <p className={label}>{t({ en: "Experience, in context", ar: "خبرة في سياقها" })}</p>
              <h2 id="about-experience" className="mt-4 text-3xl sm:text-4xl">
                {t({ en: "Supporting organizations and their teams.", ar: "دعم المؤسسات وفرقها." })}
              </h2>
              <p className="mt-5 text-muted-foreground">
                {t({
                  en: "The company-reported figures below describe IraqCert’s experience. They are not a promise of certification, buyer acceptance or a contract. The relevant issuing body makes the certification decision.",
                  ar: "تصف الأرقام التالية الخبرة التي تعلنها عراق سيرت. وهي ليست وعداً بمنح شهادة أو قبول المشتري أو الحصول على عقد. وتتخذ الجهة المانحة المعنية قرار منح الشهادة.",
                })}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6 self-center">
              {a.experience.stats.map((s) => (
                <div key={s.value} className="border-s-2 border-primary ps-5">
                  <p className="text-5xl font-semibold tracking-tight">{s.value}</p>
                  <p className="mt-3 text-sm font-semibold">{t(s.label)}</p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {t({ en: "Company-reported", ar: "وفق إفادة الشركة" })}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section className={section} aria-labelledby="about-roles">
        <p className={label}>{t({ en: "Who does what", ar: "توزيع المسؤوليات" })}</p>
        <h2 id="about-roles" className="mt-4 text-3xl sm:text-4xl">
          {t({ en: "Preparation and independent decisions.", ar: "التحضير والقرارات المستقلة." })}
        </h2>
        <div className="mt-9 grid gap-x-10 gap-y-8 sm:grid-cols-2 lg:grid-cols-4">
          {roles.map((r) => (
            <div key={r.title.en} className="border-t border-border pt-5">
              <h3 className="text-lg">{t(r.title)}</h3>
              <p className="mt-3 text-sm text-muted-foreground">{t(r.text)}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="border-y border-border bg-[#f5f3ef]" aria-labelledby="about-reach">
        <div className={section}>
          <div className="grid gap-10 lg:grid-cols-[1fr_1.3fr] lg:gap-20">
            <div>
              <p className={label}>{t(a.reach.eyebrow)}</p>
              <h2 id="about-reach" className="mt-4 text-3xl sm:text-4xl">
                {t(a.reach.title)}
              </h2>
              <p className="mt-5 text-muted-foreground">{t(a.reach.subtitle)}</p>
              <p className="mt-4 text-sm text-muted-foreground">
                {t({
                  en: "Contact us first. Tell us where your team and sites are based, what the buyer requires and your target date. Confirm any site visits and assessment arrangements when agreeing the scope; the Erbil base does not receive walk-in visitors.",
                  ar: "تواصل معنا أولاً وحدّد مواقع فريقك ومنشآتك ومتطلبات المشتري والموعد المستهدف. أكّد الزيارات وترتيبات التقييم عند الاتفاق على النطاق؛ مقر العمل في أربيل لا يستقبل الزيارات المباشرة.",
                })}
              </p>
            </div>
            <div className="divide-y divide-border border-y border-border">
              {cities.map((c) => (
                <LangLink
                  key={c.slug}
                  to={`/services/iso-certification/${c.slug}`}
                  className="flex min-h-24 items-center justify-between gap-5 py-5 transition-colors hover:text-primary"
                >
                  <span className="text-xl font-semibold">{t(c.name)}</span>
                  <span className="max-w-[14rem] text-sm text-muted-foreground">{t(c.detail)}</span>
                </LangLink>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section className={section} aria-labelledby="about-certification">
        <div className="grid gap-10 lg:grid-cols-[1.3fr_1fr] lg:gap-20">
          <div>
            <p className={label}>{t(a.accreditation.eyebrow)}</p>
            <h2 id="about-certification" className="mt-4 text-3xl">
              {t(a.accreditation.title)}
            </h2>
            <p className="mt-5 text-muted-foreground">{t(a.accreditation.body)}</p>
            <a
              href={a.accreditation.verifyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex min-h-11 items-center font-semibold text-primary underline underline-offset-4"
            >
              {t(a.accreditation.verify)}
            </a>
          </div>
          <aside className="rounded-md border border-border p-6">
            <p className="text-sm font-semibold">
              {t({
                en: "Company-disclosed partner information",
                ar: "معلومات الجهة الشريكة وفق إفادة الشركة",
              })}
            </p>
            <p className="mt-3 text-sm text-muted-foreground">
              {t(a.accreditation.numberLabel)}: <bdi>{a.accreditation.number}</bdi>
            </p>
            <p className="mt-4 text-sm text-muted-foreground">
              {t(a.accreditation.scopeLabel)}: {a.accreditation.scope.join(" · ")}
            </p>
            <p className="mt-4 text-xs text-muted-foreground">
              {t({
                en: "Confirm the current accreditation record and proposed issuer before booking. Company disclosure is not independent verification of the reference or partnership.",
                ar: "أكّد سجل الاعتماد الحالي والجهة المانحة المقترحة قبل الحجز. لا تُعدّ إفادة الشركة تحققاً مستقلاً من المرجع أو العلاقة مع الجهة الشريكة.",
              })}
            </p>
          </aside>
        </div>
      </section>
      <FaqSection items={content.faq.about} className="bg-background" />
      <section className="border-t border-border bg-[#f5f3ef]">
        <div
          className={`${section} flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between`}
        >
          <div>
            <h2 className="text-2xl">
              {t({
                en: "Start with your team and your requirement.",
                ar: "ابدأ بفريقك ومتطلباتك.",
              })}
            </h2>
            <p className="mt-3 max-w-xl text-muted-foreground">
              {t({
                en: "Share your activities, sites and buyer’s request so we can discuss the appropriate preparation scope.",
                ar: "شارك أنشطتك ومواقعك وطلب المشتري لنناقش نطاق التحضير المناسب.",
              })}
            </p>
          </div>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-12 shrink-0 items-center justify-center rounded-md bg-primary px-6 py-3 font-semibold text-primary-foreground"
          >
            {t(content.hero.cta)}
          </a>
        </div>
      </section>
    </>
  );
}
